// modules/website/WebsiteRouter.tsx - Roteador Principal do Site Multi-Tenant

import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { DynamicPage } from './pages/DynamicPage';
import { useTenant } from './hooks/useTenant';
import { TenantLayout } from './components/TenantLayout';

import { useAuth } from '../auth/AuthContext';

export const WebsiteRouter: React.FC = () => {
  const { tenant, loading, error } = useTenant();
  const { user, loading: authLoading } = useAuth();


  // Aplicar tema do tenant
  React.useEffect(() => {
    if (tenant?.branding) {
      // Aplicar CSS customizado
      const style = document.createElement('style');
      style.innerHTML = `
        :root {
          --color-primary: ${tenant.branding.primaryColor};
          --color-secondary: ${tenant.branding.secondaryColor};
          --color-accent: ${tenant.branding.accentColor};
          --font-family: ${tenant.branding.fontFamily}, sans-serif;
        }
        body {
          font-family: var(--font-family);
        }
      `;
      document.head.appendChild(style);

      // Atualizar título da página
      document.title = tenant.seoTitle || tenant.businessName;

      // Meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', tenant.seoDescription || tenant.description);
      }

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [tenant]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando site...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-6">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Site Não Encontrado
          </h1>
          <p className="text-gray-600 mb-6">
            {error || 'Este domínio não está configurado ou foi desativado.'}
          </p>
          <a
            href="https://nossapesca.com"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Ir para Nossa Pesca
          </a>
        </div>
      </div>
    );
  }



  // Private Site Guard
  if (tenant && tenant.settings?.privacy === 'private' && !user && !authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-6 bg-white p-8 rounded-xl shadow-lg border border-gray-100">
          <div className="text-5xl mb-6">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Este site é privado
          </h1>
          <p className="text-gray-600 mb-8">
            Para acessar o conteúdo deste grupo, você precisa estar logado na plataforma.
          </p>
          <a
            href={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
            className="inline-block w-full bg-brand-blue-deep text-white px-6 py-3 rounded-lg hover:bg-brand-blue-dark font-medium transition-colors"
          >
            Fazer Login ou Cadastrar
          </a>
        </div>
      </div>
    );
  }

  return (
    <TenantLayout tenant={tenant}>
      <Routes>
        {/* Página inicial (home) */}
        <Route path="/" element={<DynamicPage />} />
        
        {/* Páginas dinâmicas */}
        <Route path="/:slug" element={<DynamicPage />} />
        
        {/* Rotas especiais */}
        <Route path="/admin/*" element={<AdminRedirect />} />
      </Routes>
    </TenantLayout>
  );
};

// Componente auxiliar para redirecionar para admin
const AdminRedirect: React.FC = () => {
  React.useEffect(() => {
    // Redirecionar para painel admin (implementar depois)
    window.location.href = '/admin/dashboard';
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p>Redirecionando para o painel administrativo...</p>
    </div>
  );
};
